import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms'; 
import { MsalService } from '@azure/msal-angular';
import { AuthService } from '../../services/auth.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [FormsModule, MatProgressSpinnerModule]
})
export class LoginComponent {
  username: string = '';
  password: string = '';
  errorMessage = '';
  loggingIn: boolean = false;

  constructor(private msalService: MsalService, private authService: AuthService) {}

  ngOnInit() {
    const msalRedirectActive = sessionStorage.getItem("authIndicator") === "Busy";

    if (msalRedirectActive) {
      this.loggingIn = true; // Suppress login UI
    }
    this.msalService.handleRedirectObservable().subscribe({
      next: async (result) => {
        if (result) {
          try {
          // recommended: set active account
          if (result.account) {
            this.msalService.instance.setActiveAccount(result.account);
          }

          // ✅ start the 8-hour hard session window
          this.authService.startAppSession();

          // OPTIONAL: Get tokens immediately if your UI needs them right away
          const graphToken = await this.getAccessToken();
          const sqlToken = await this.getSqlAccessToken();
          const formsToken = await this.getFormsAccessToken();

          if (graphToken) localStorage.setItem('authToken', graphToken);
          if (sqlToken) localStorage.setItem('ms_authToken', sqlToken);
          if (formsToken) localStorage.setItem('forms_authToken', formsToken);

          if (!graphToken || !sqlToken) throw new Error('Microsoft sign-in could not be completed.');
          await this.authService.getUserDetails(graphToken);
          await this.authService.completeLogin();

          // ✅ remove this now (recommended):
          // setInterval(() => { this.getRefreshTokens(); }, (1000 * 60 * 8));
          sessionStorage.removeItem('authIndicator');
          } catch {
            this.authService.logout();
            this.errorMessage = 'Your Microsoft session ended. Please sign in again.';
            this.loggingIn = false;
          }
        }
      },
      error: (error) => {
        sessionStorage.removeItem("authIndicator");
        this.loggingIn = false;
        console.error('Login failed', error);
      }
    });
  }

  // Get Access Token from MSAL using refresh token once logged in
  async getAccessToken(): Promise<string | null> {
    return this.getValidToken(['User.Read', 'GroupMember.Read.All']);
  }

  async getSqlAccessToken(): Promise<string | null> {
    return this.getValidToken(['https://database.windows.net/.default']);
  }

  async getFormsAccessToken(): Promise<string | null> {
    return this.getValidToken(['https://forms.office.com/.default']);
  }

  // Get tokens to maintain logged in state
  async getRefreshTokens() {
    // If token keep alive is more than 10 hours, log the user out
    if (Number(localStorage.getItem('token_refreshed')) > 75) {
      this.authService.logout();
    } else {
      // Get new tokens on interval
      try {
        await this.getAccessToken().then(
          (result) => {
            localStorage.setItem('authToken',result!);
          }
        );
        await this.getSqlAccessToken().then(
          (result) => {
              localStorage.setItem('ms_authToken', result!);
          }
        );

        await this.getFormsAccessToken().then(
          (result) => {
            if (result) {
              localStorage.setItem('forms_authToken', result);
            }
          }
        );

        localStorage.setItem('token_refreshed', (Number(localStorage.getItem('token_refreshed')) + 1).toString())
      } catch (ex) {
        console.error('Login Expired', ex);
        this.authService.logout();
      }
    }
  }

  // Execute login attempt and start MSAL flow
  async login() {
    this.loggingIn = true;
    this.username = this.username.includes('@') ? this.username : `${this.username}@royalny.com`;
    sessionStorage.setItem("authIndicator", "Busy");
    await this.msalService.initialize().subscribe({
      next: () => {
        this.msalService.loginRedirect({
          scopes:['User.Read','GroupMember.Read.All'],
          loginHint: this.username,
          prompt: 'login'
        }); // Change this from loginPopup() to loginRedirect()
      },
      error: (error) => console.error('Login failed', error)
    });
  }
  
private async getValidToken(scopes: string[]): Promise<string | null> {
    // ✅ hard stop after 8 hours
    if (this.authService.isAppSessionExpired()) {
      this.authService.logout();
      return null;
    }

    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (!account) return null;

    try {
      // ✅ This will return cached token if still valid,
      // or silently renew if expired (normal MSAL pattern). [1](https://learn.microsoft.com/en-us/entra/msal/javascript/browser/token-lifetimes)
      const result = await this.msalService.instance.acquireTokenSilent({
        account,
        scopes,
      });

      return result.accessToken;
    } catch (e: any) {
      // If Entra requires interaction (MFA, CA, etc.), you decide what to do:
      if (e instanceof InteractionRequiredAuthError) {
        // Option A: force re-login
        this.authService.logout();
        // Option B (alternative): this.msalService.loginRedirect({ scopes });
        return null;
      }

      console.error('Silent token acquisition failed', e);
      this.authService.logout();
      return null;
    }
  }

}
